import { TIER_ORDER } from '../consts';
import { USE_TOP_PERFORMER_MOCK } from '../mockData';
import { DisplayTier, PerformerStatus } from '../types';
import {
  EvaluationMonth,
  GoalState,
  PerformanceTier,
  PerformerStatusEligible,
  PerformerStatusIneligible,
  PerformerStatusNotEvaluated,
  PerformerStatusType,
  TargetingValues,
} from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/models';

export { getMonthName } from './getMonthName';
export { getNextMonth } from './getNextMonth';

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

/** A status for a merchant that was evaluated, i.e. holding one of the performance tiers. */
export function isEligible(status: PerformerStatus): status is PerformerStatusEligible {
  return status.type === PerformerStatusType.ELIGIBLE;
}

/** A status for a merchant that was NOT evaluated. */
export function isIneligible(status: PerformerStatus): status is PerformerStatusIneligible {
  return status.type === PerformerStatusType.INELIGIBLE;
}

/** A status for a merchant that was NOT evaluated. */
export function isNotEvaluated(status: PerformerStatus): status is PerformerStatusNotEvaluated {
  return status.type === PerformerStatusType.NOT_EVALUATED;
}

export function getDisplayTier(status: PerformerStatus): DisplayTier {
  return isEligible(status) ? status.tier : 'ineligible';
}

export function countCompletedGoals(status: PerformerStatus): number {
  if (!isEligible(status)) {
    return 0;
  }

  return status.goal_statuses.filter((goal) => goal.state === GoalState.COMPLETED).length;
}

/** The tier directly above `tier` if one exists. The same tier when it is already the highest. undefined if uneligible. */
export function getNextTier(tier: DisplayTier): PerformanceTier | undefined {
  const currentTierIndex = TIER_ORDER.findIndex((orderedTier) => orderedTier === tier);
  if (currentTierIndex < 0) {
    return undefined;
  }
  if (currentTierIndex === TIER_ORDER.length - 1) {
    return TIER_ORDER[currentTierIndex];
  }
  return TIER_ORDER[currentTierIndex + 1];
}

/**
 * Days left in an evaluation month. The API reports the period as a year/month pair rather than an
 * end timestamp, so the deadline is the first instant of the following month.
 */
export function getDaysLeftInPeriod({ year, month }: EvaluationMonth): number {
  const periodEnd = new Date(year, month, 1);
  return Math.max(0, Math.ceil((periodEnd.getTime() - Date.now()) / MILLISECONDS_PER_DAY));
}

/** Mocked data bypasses the targeting flag, so the screens are reachable without it. */
export function isTopPerformerEnabled(targetingParameters: TargetingValues) {
  return (
    USE_TOP_PERFORMER_MOCK ||
    Boolean(targetingParameters.delivery_provider_portal_flags.show_top_performer_content)
  );
}

export function isTopPerformerOnboarding(targetingParameters: TargetingValues): boolean {
  return Boolean(targetingParameters.delivery_provider_portal_flags.show_top_performer_onboarding);
}
